// @catalog F.047
// Cliente de la importación de exports oficiales de Mercado Libre.
// Contrato de frontera: backend/src/mercadolibre/dto/marketplace-ml.dto.ts (M2).
import { apiClient } from "./apiClient";

export type ImportBatchStatus = "procesando" | "completado" | "error";

export type Marketplace = "mercado_libre" | "amazon" | "walmart";

export type MlOperationStatus =
  | "preparacion"
  | "en_camino"
  | "entregado"
  | "devuelto_reembolsado"
  | "cancelado"
  | "desconocido";

export interface ImportBatch {
  id: number;
  fileName: string;
  // Plataforma autodetectada por el backend (F.048); null mientras se detecta.
  marketplace: Marketplace | null;
  fileHash: string;
  status: ImportBatchStatus;
  errorDetail: string | null;
  rowsNew: number;
  rowsUpdated: number;
  rowsUnchanged: number;
  sheetsProcessed: number;
  sheetsRejected: Array<{ sheet: string; reason: string }>;
  rowsMatched: number;
  rowsUnmatched: number;
  createdAt: string;
  finishedAt: string | null;
}

export interface OrderRow {
  orderId: string;
  saleId: string;
  saleDate: string;
  status: MlOperationStatus;
  statusRaw: string | null;
  grossProduct: number;
  commissionPure: number;
  withholdingIva: number;
  withholdingIsr: number;
  shipNet: number;
  refunds: number;
  totalReceived: number;
  odooPosOrderId: string | null;
  odooCostTotal: number | null;
  marginReal: number | null;
  marginPostWithholdings: number | null;
}

export interface OrdersPage {
  rows: OrderRow[];
  total: number;
  page: number;
  pageSize: number;
  matchRate: number;
  unmatchedCount: number;
}

export interface OrdersFilters {
  month: string; // YYYY-MM
  status?: MlOperationStatus | undefined;
  unmatched?: boolean | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export interface MonthlySummaryRow {
  month: string;
  ordersCount: number;
  gross: number;
  commissionPure: number;
  withholdingIva: number;
  withholdingIsr: number;
  shipNet: number;
  refunds: number;
  totalReceived: number;
  odooCostTotal: number | null;
  marginReal: number | null;
  marginPostWithholdings: number | null;
  matchRate: number;
}

export interface AmazonItemRow {
  orderId: string;
  orderItemId: string;
  purchaseDate: string;
  sku: string | null;
  productName: string | null;
  quantity: number | null;
  currency: string | null;
  itemPrice: number;
  itemTax: number;
  shippingPrice: number;
  shippingTax: number;
  shipCity: string | null;
  shipState: string | null;
  salesChannel: string | null;
}

export interface AmazonItemsPage {
  rows: AmazonItemRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface WalmartLineRow {
  orderNumber: string;
  poId: string;
  lineNumber: number;
  orderDate: string;
  shipDate: string | null;
  deliveryDate: string | null;
  status: MlOperationStatus;
  statusRaw: string | null;
  sku: string | null;
  productName: string | null;
  quantity: number | null;
  unitPrice: number;
  shippingCost: number | null;
  carrier: string | null;
  trackingNumber: string | null;
}

export interface WalmartLinesPage {
  rows: WalmartLineRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PlatformLinesFilters {
  month?: string | undefined; // YYYY-MM; sin mes = todas
  page?: number | undefined;
  pageSize?: number | undefined;
}

const MercadoLibreImportService = {
  async uploadImport(file: File): Promise<ImportBatch> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post<ImportBatch>(
      "/mercadolibre/imports",
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },

  async listImports(limit = 20): Promise<ImportBatch[]> {
    const { data } = await apiClient.get<ImportBatch[]>(
      "/mercadolibre/imports",
      { params: { limit } },
    );
    return data;
  },

  async getImport(id: number): Promise<ImportBatch> {
    const { data } = await apiClient.get<ImportBatch>(
      `/mercadolibre/imports/${id}`,
    );
    return data;
  },

  async getOrders(filters: OrdersFilters): Promise<OrdersPage> {
    const { data } = await apiClient.get<OrdersPage>("/mercadolibre/orders", {
      params: {
        month: filters.month,
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.unmatched ? { unmatched: "true" } : {}),
        ...(filters.page ? { page: filters.page } : {}),
        ...(filters.pageSize ? { pageSize: filters.pageSize } : {}),
      },
    });
    return data;
  },

  async getMonthlySummary(
    fromMonth?: string,
    toMonth?: string,
  ): Promise<MonthlySummaryRow[]> {
    const { data } = await apiClient.get<MonthlySummaryRow[]>(
      "/mercadolibre/summary/monthly",
      {
        params: {
          ...(fromMonth ? { fromMonth } : {}),
          ...(toMonth ? { toMonth } : {}),
        },
      },
    );
    return data;
  },

  async getAmazonItems(filters: PlatformLinesFilters): Promise<AmazonItemsPage> {
    const { data } = await apiClient.get<AmazonItemsPage>(
      "/mercadolibre/amazon/items",
      {
        params: {
          ...(filters.month ? { month: filters.month } : {}),
          ...(filters.page ? { page: filters.page } : {}),
          ...(filters.pageSize ? { pageSize: filters.pageSize } : {}),
        },
      },
    );
    return data;
  },

  async getWalmartLines(filters: PlatformLinesFilters): Promise<WalmartLinesPage> {
    const { data } = await apiClient.get<WalmartLinesPage>(
      "/mercadolibre/walmart/lines",
      {
        params: {
          ...(filters.month ? { month: filters.month } : {}),
          ...(filters.page ? { page: filters.page } : {}),
          ...(filters.pageSize ? { pageSize: filters.pageSize } : {}),
        },
      },
    );
    return data;
  },

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    return value.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    });
  },
};

export default MercadoLibreImportService;
